import React, { useEffect, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useSnapshot } from "valtio";
import store, { UserSearchFilters } from "../store";
import { userSearch } from "../hooks/userSearch";

const SearchBarContainer = styled.header`
  ${tw`flex bg-gray-900 py-8 px-5 justify-center`}
`;
const SearchInput = styled.form`
  ${tw`w-full max-w-4xl mx-auto`}

  input,
  select {
    ${tw`w-full rounded border-2 border-transparent px-4 py-3 font-medium focus:outline-none transition duration-300 focus:border-primary-500 hover:border-gray-500`}
  }

  button {
    ${tw`w-full bg-primary-600 text-gray-100 font-bold rounded py-3 flex items-center justify-center focus:outline-none hover:bg-primary-800 transition duration-300`}
  }
`;

const PrimarySearchRow = tw.div`grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3`;

const FiltersRow = tw.div`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-3`;

const initialFilters: UserSearchFilters = {
  language: "",
  minFollowers: "",
  minRepos: "",
  accountType: "",
  sort: "",
};

export const SearchBar: React.FC = () => {
  const snap = useSnapshot(store);
  const [location, setLocation] = useState(store.userLocation);
  const [filters, setFilters] = useState<UserSearchFilters>(initialFilters);
  const {
    language,
    minFollowers,
    minRepos,
    accountType,
    sort,
  } = snap.filters;

  useEffect(() => {
    userSearch(snap.userLocation, snap.page, undefined, {
      language,
      minFollowers,
      minRepos,
      accountType,
      sort,
    });
  }, [
    snap.userLocation,
    snap.page,
    language,
    minFollowers,
    minRepos,
    accountType,
    sort,
  ]);

  const updateFilter = <Key extends keyof UserSearchFilters>(
    key: Key,
    value: UserSearchFilters[Key]
  ) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value,
    }));
  };

  function handleSubmit() {
    store.page = 1;
    store.userLocation = location.trim();
    store.filters = {
      language: filters.language.trim(),
      minFollowers: filters.minFollowers,
      minRepos: filters.minRepos,
      accountType: filters.accountType,
      sort: filters.sort,
    };
  }

  return (
    <SearchBarContainer>
      <SearchInput
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <PrimarySearchRow>
          <input
            aria-label="Search GitHub users by location"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            name="search"
            id="search"
            type="search"
            placeholder="Location"
          />
          <button type="submit">Search</button>
        </PrimarySearchRow>
        <FiltersRow>
          <input
            aria-label="Filter by programming language"
            value={filters.language}
            onChange={(event) => updateFilter("language", event.target.value)}
            type="search"
            placeholder="Language"
          />
          <input
            aria-label="Minimum followers"
            value={filters.minFollowers}
            onChange={(event) =>
              updateFilter("minFollowers", event.target.value)
            }
            min="0"
            type="number"
            placeholder="Min followers"
          />
          <input
            aria-label="Minimum repositories"
            value={filters.minRepos}
            onChange={(event) => updateFilter("minRepos", event.target.value)}
            min="0"
            type="number"
            placeholder="Min repos"
          />
          <select
            aria-label="Account type"
            value={filters.accountType}
            onChange={(event) =>
              updateFilter(
                "accountType",
                event.target.value as UserSearchFilters["accountType"]
              )
            }
          >
            <option value="">All accounts</option>
            <option value="user">Users only</option>
            <option value="org">Organizations only</option>
          </select>
          <select
            aria-label="Sort results"
            value={filters.sort}
            onChange={(event) =>
              updateFilter(
                "sort",
                event.target.value as UserSearchFilters["sort"]
              )
            }
          >
            <option value="">Best match</option>
            <option value="followers">Most followers</option>
            <option value="repositories">Most repos</option>
            <option value="joined">Newest accounts</option>
          </select>
        </FiltersRow>
      </SearchInput>
    </SearchBarContainer>
  );
};

export default SearchBar;
